/**
 * CommandPalette - T048
 *
 * Keyboard-driven command palette (⌘K / Ctrl+K) with global search
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../../services/api';
import { formatCurrency } from '@armard/shared';

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

interface Command {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  shortcut?: string;
  action: () => void;
  keywords?: string[];
}

interface SearchResult {
  id: string;
  label: string;
  description?: string;
  section: 'commands' | 'customers' | 'invoices' | 'contracts' | 'estimates';
  action: () => void;
}

interface SectionState {
  loading: boolean;
  results: SearchResult[];
}

// ─────────────────────────────────────────────────────────────
// STATIC COMMANDS
// ─────────────────────────────────────────────────────────────

const staticCommands: Command[] = [
  {
    id: 'home',
    label: 'Dashboard',
    description: 'Zur Übersicht',
    shortcut: 'G D',
    keywords: ['start', 'übersicht'],
    action: () => {},
  },
  {
    id: 'customers',
    label: 'Kunden',
    description: 'Kundenliste anzeigen',
    shortcut: 'G K',
    keywords: ['kunde', 'client'],
    action: () => {},
  },
  {
    id: 'invoices',
    label: 'Rechnungen',
    description: 'Rechnungsliste anzeigen',
    shortcut: 'G R',
    keywords: ['rechnung', 'invoice'],
    action: () => {},
  },
  {
    id: 'new-invoice',
    label: 'Neue Rechnung',
    description: 'Rechnung erstellen',
    shortcut: 'N R',
    keywords: ['neu', 'erstellen', 'create'],
    action: () => {},
  },
  {
    id: 'contracts',
    label: 'Verträge',
    description: 'Vertragsliste anzeigen',
    shortcut: 'G V',
    keywords: ['vertrag', 'contract', 'recurring'],
    action: () => {},
  },
  {
    id: 'new-contract',
    label: 'Neuer Vertrag',
    description: 'Vertrag erstellen',
    shortcut: 'N V',
    keywords: ['neu', 'erstellen', 'create', 'contract'],
    action: () => {},
  },
  {
    id: 'estimates',
    label: 'Angebote',
    description: 'Angebotsliste anzeigen',
    shortcut: 'G A',
    keywords: ['angebot', 'estimate', 'quote', 'kostenvoranschlag'],
    action: () => {},
  },
  {
    id: 'new-estimate',
    label: 'Neues Angebot',
    description: 'Angebot erstellen',
    shortcut: 'N A',
    keywords: ['neu', 'erstellen', 'create', 'angebot', 'quote'],
    action: () => {},
  },
  {
    id: 'reminders',
    label: 'Mahnungen',
    description: 'Mahnungs-Übersicht',
    shortcut: 'G M',
    keywords: ['mahnung', 'reminder', 'overdue', 'überfällig'],
    action: () => {},
  },
  {
    id: 'audit-log',
    label: 'Audit-Protokoll',
    description: 'Änderungs-Historie',
    shortcut: 'G L',
    keywords: ['audit', 'log', 'history', 'historie', 'protokoll', 'aktivität'],
    action: () => {},
  },
  {
    id: 'export',
    label: 'Export',
    description: 'DATEV / CSV Export',
    shortcut: 'G X',
    keywords: ['export', 'datev', 'csv', 'download'],
    action: () => {},
  },
  {
    id: 'settings',
    label: 'Einstellungen',
    description: 'Systemeinstellungen',
    shortcut: 'G E',
    keywords: ['settings', 'config'],
    action: () => {},
  },
  // View / filter action commands — deep-link into /invoices with a
  // specific view configuration applied.
  {
    id: 'invoices-group-quarter',
    label: 'Rechnungen nach Quartal gruppieren',
    description: 'Liste mit Quartals-Summen',
    keywords: ['quartal', 'gruppieren', 'group', 'quarter', 'summe'],
    action: () => {},
  },
  {
    id: 'invoices-active',
    label: 'Aktive Rechnungen',
    description: 'Offene, gesendete, überfällige',
    keywords: ['aktiv', 'offen', 'laufend', 'active'],
    action: () => {},
  },
  {
    id: 'invoices-overdue',
    label: 'Überfällige Rechnungen',
    description: 'Nur überfällige',
    keywords: ['überfällig', 'overdue', 'mahn'],
    action: () => {},
  },
  {
    id: 'invoices-deferred',
    label: 'Rückgestellte Rechnungen',
    description: 'Nur zurückgestellte',
    keywords: ['rückgestellt', 'deferred', 'wartend', 'pause'],
    action: () => {},
  },
  {
    id: 'invoices-drafts',
    label: 'Entwürfe',
    description: 'Nur nicht gesendete Entwürfe',
    keywords: ['entwurf', 'draft', 'unfertig'],
    action: () => {},
  },
];

// ─────────────────────────────────────────────────────────────
// SECTION LABELS
// ─────────────────────────────────────────────────────────────

const sectionLabels: Record<string, string> = {
  commands: 'Befehle',
  customers: 'Kunden',
  invoices: 'Rechnungen',
  contracts: 'Verträge',
  estimates: 'Angebote',
};

// ─────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [sections, setSections] = useState<Record<string, SectionState>>({
    customers: { loading: false, results: [] },
    invoices: { loading: false, results: [] },
    contracts: { loading: false, results: [] },
    estimates: { loading: false, results: [] },
  });

  // Navigation map for static commands. Values are either a bare path
  // (navigate to) or a `[path, search]` tuple (navigate to path with
  // structured search params — used by view/filter action commands to
  // deep-link into a pre-configured list view).
  const navigationMap: Record<string, string | [string, Record<string, unknown>]> = useMemo(() => ({
    home: '/',
    customers: '/customers',
    invoices: '/invoices',
    'new-invoice': '/invoices/new',
    contracts: '/contracts',
    'new-contract': '/contracts/new',
    estimates: '/estimates',
    'new-estimate': '/estimates/new',
    reminders: '/reminders',
    'audit-log': '/audit-log',
    export: '/export',
    settings: '/settings',
    // Action commands — deep-link variants that land on /invoices with a
    // specific view configuration applied via URL search params.
    'invoices-group-quarter': ['/invoices', { groupBy: 'quarter', page: 1 }],
    'invoices-active': ['/invoices', { status: 'active', page: 1 }],
    'invoices-overdue': ['/invoices', { status: 'overdue', page: 1 }],
    'invoices-deferred': ['/invoices', { status: 'deferred', page: 1 }],
    'invoices-drafts': ['/invoices', { status: 'draft', page: 1 }],
  }), []);

  // Filter static commands locally
  const filteredCommands: SearchResult[] = useMemo(() => {
    const cmds = staticCommands.map((cmd) => ({
      id: `cmd-${cmd.id}`,
      label: cmd.label,
      description: cmd.description,
      shortcut: cmd.shortcut,
      section: 'commands' as const,
      action: () => {
        const entry = navigationMap[cmd.id];
        if (typeof entry === 'string') {
          navigate({ to: entry });
        } else if (Array.isArray(entry)) {
          const [path, search] = entry;
          navigate({ to: path, search });
        } else {
          cmd.action();
        }
      },
    }));

    if (!query) return cmds;

    const lowerQuery = query.toLowerCase();
    return cmds.filter((cmd) => {
      const original = staticCommands.find((c) => `cmd-${c.id}` === cmd.id);
      const labelMatch = cmd.label.toLowerCase().includes(lowerQuery);
      const descMatch = cmd.description?.toLowerCase().includes(lowerQuery);
      const keywordMatch = original?.keywords?.some((k) => k.toLowerCase().includes(lowerQuery));
      return labelMatch || descMatch || keywordMatch;
    });
  }, [query, navigate, navigationMap]);

  // Perform API search when query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (query.length < 2) {
      setSections({
        customers: { loading: false, results: [] },
        invoices: { loading: false, results: [] },
        contracts: { loading: false, results: [] },
        estimates: { loading: false, results: [] },
      });
      return;
    }

    // Mark all sections as loading
    setSections((prev) => ({
      customers: { ...prev.customers, loading: true },
      invoices: { ...prev.invoices, loading: true },
      contracts: { ...prev.contracts, loading: true },
      estimates: { ...prev.estimates, loading: true },
    }));

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;

      // Search customers
      api.get<{ id: string; customerNumber: string; companyName: string; email: string | null }[]>(
        `/customers/search?q=${encodeURIComponent(query)}&limit=5`
      ).then((data) => {
        if (controller.signal.aborted) return;
        setSections((prev) => ({
          ...prev,
          customers: {
            loading: false,
            results: data.map((c) => ({
              id: `cust-${c.id}`,
              label: c.companyName,
              description: c.customerNumber + (c.email ? ` · ${c.email}` : ''),
              section: 'customers' as const,
              action: () => navigate({ to: '/customers/$id', params: { id: c.id } }),
            })),
          },
        }));
      }).catch(() => {
        if (!controller.signal.aborted) {
          setSections((prev) => ({ ...prev, customers: { loading: false, results: [] } }));
        }
      });

      // Search invoices
      api.get<{ invoices: { id: string; invoiceNumber: string; grossTotal: string; status: string; customer?: { companyName: string } }[] }>(
        `/invoices?q=${encodeURIComponent(query)}&limit=5`
      ).then((data) => {
        if (controller.signal.aborted) return;
        setSections((prev) => ({
          ...prev,
          invoices: {
            loading: false,
            results: data.invoices.map((inv) => ({
              id: `inv-${inv.id}`,
              label: inv.invoiceNumber,
              description: `${inv.customer?.companyName ?? ''} · ${formatCurrency(Number(inv.grossTotal))}`,
              section: 'invoices' as const,
              action: () => navigate({ to: '/invoices/$id', params: { id: inv.id } }),
            })),
          },
        }));
      }).catch(() => {
        if (!controller.signal.aborted) {
          setSections((prev) => ({ ...prev, invoices: { loading: false, results: [] } }));
        }
      });

      // Search contracts
      api.get<{ contracts: { id: string; contractNumber: string; title: string; customer?: { companyName: string } }[] }>(
        `/contracts?search=${encodeURIComponent(query)}&limit=5`
      ).then((data) => {
        if (controller.signal.aborted) return;
        setSections((prev) => ({
          ...prev,
          contracts: {
            loading: false,
            results: data.contracts.map((c) => ({
              id: `con-${c.id}`,
              label: `${c.contractNumber} – ${c.title}`,
              description: c.customer?.companyName,
              section: 'contracts' as const,
              action: () => navigate({ to: '/contracts/$id', params: { id: c.id } }),
            })),
          },
        }));
      }).catch(() => {
        if (!controller.signal.aborted) {
          setSections((prev) => ({ ...prev, contracts: { loading: false, results: [] } }));
        }
      });

      // Search estimates
      api.get<{ estimates: { id: string; estimateNumber: string; subject: string | null; customer?: { companyName: string } }[] }>(
        `/estimates?search=${encodeURIComponent(query)}&limit=5`
      ).then((data) => {
        if (controller.signal.aborted) return;
        setSections((prev) => ({
          ...prev,
          estimates: {
            loading: false,
            results: data.estimates.map((e) => ({
              id: `est-${e.id}`,
              label: e.estimateNumber,
              description: [e.customer?.companyName, e.subject].filter(Boolean).join(' · ') || undefined,
              section: 'estimates' as const,
              action: () => navigate({ to: '/estimates/$id', params: { id: e.id } }),
            })),
          },
        }));
      }).catch(() => {
        if (!controller.signal.aborted) {
          setSections((prev) => ({ ...prev, estimates: { loading: false, results: [] } }));
        }
      });
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query, navigate]);

  // Build flat list of all results for keyboard navigation
  const allResults = useMemo(() => {
    const flat: (SearchResult & { shortcut?: string })[] = [];

    // Commands first
    if (filteredCommands.length > 0) {
      for (const cmd of filteredCommands) {
        const original = staticCommands.find((c) => `cmd-${c.id}` === cmd.id);
        flat.push({ ...cmd, shortcut: original?.shortcut });
      }
    }

    // Then entity results
    for (const sectionKey of ['customers', 'invoices', 'contracts', 'estimates'] as const) {
      const section = sections[sectionKey];
      if (section.results.length > 0) {
        flat.push(...section.results);
      }
    }

    return flat;
  }, [filteredCommands, sections]);

  // Group results by section for rendering
  const groupedResults = useMemo(() => {
    const groups: { section: string; label: string; results: (SearchResult & { shortcut?: string })[]; loading: boolean }[] = [];

    if (filteredCommands.length > 0) {
      groups.push({
        section: 'commands',
        label: sectionLabels.commands,
        results: filteredCommands.map((cmd) => {
          const original = staticCommands.find((c) => `cmd-${c.id}` === cmd.id);
          return { ...cmd, shortcut: original?.shortcut };
        }),
        loading: false,
      });
    }

    for (const sectionKey of ['customers', 'invoices', 'contracts', 'estimates'] as const) {
      const section = sections[sectionKey];
      if (section.results.length > 0 || section.loading) {
        groups.push({
          section: sectionKey,
          label: sectionLabels[sectionKey],
          results: section.results,
          loading: section.loading,
        });
      }
    }

    return groups;
  }, [filteredCommands, sections]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [allResults.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (!resultsRef.current) return;
    const selected = resultsRef.current.querySelector('[data-selected="true"]');
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Close and reset
  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setSections({
      customers: { loading: false, results: [] },
      invoices: { loading: false, results: [] },
      contracts: { loading: false, results: [] },
      estimates: { loading: false, results: [] },
    });
  }, []);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Open with Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          close();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < allResults.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : allResults.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (allResults[selectedIndex]) {
            allResults[selectedIndex].action();
            close();
          }
          break;
      }
    },
    [isOpen, allResults, selectedIndex, close]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Track flat index for keyboard navigation
  let flatIndex = -1;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={close}
          />

          {/* Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-lg bg-zinc-900/95 border border-white/10 rounded-xl shadow-2xl overflow-hidden"
          >
            {/* Search input */}
            <div className="flex items-center px-4 border-b border-white/10">
              <SearchIcon className="w-5 h-5 text-white/40" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Suchen oder Befehl eingeben..."
                className="w-full px-3 py-4 bg-transparent text-white placeholder:text-white/40 outline-none ring-0 border-none focus:outline-none focus:ring-0 focus:border-none focus-visible:outline-none"
                style={{ outline: 'none' }}
                autoFocus
              />
              {query.length >= 2 && Object.values(sections).some((s) => s.loading) && (
                <Spinner className="w-4 h-4 text-white/40 mr-2" />
              )}
              <kbd className="hidden sm:inline-flex px-2 py-1 text-xs text-white/40 bg-white/5 rounded">
                ESC
              </kbd>
            </div>

            {/* Results */}
            <div ref={resultsRef} className="max-h-80 overflow-y-auto py-2">
              {allResults.length === 0 && !Object.values(sections).some((s) => s.loading) ? (
                <div className="px-4 py-8 text-center text-white/40">
                  {query.length >= 2 ? 'Keine Ergebnisse gefunden' : 'Keine Befehle gefunden'}
                </div>
              ) : (
                groupedResults.map((group) => (
                  <div key={group.section}>
                    {/* Section header */}
                    <div className="px-4 pt-3 pb-1 text-xs font-medium text-white/30 uppercase tracking-wider">
                      {group.label}
                      {group.loading && (
                        <Spinner className="w-3 h-3 text-white/30 inline-block ml-2" />
                      )}
                    </div>

                    {/* Section results */}
                    {group.results.map((result) => {
                      flatIndex++;
                      const isSelected = flatIndex === selectedIndex;
                      const currentFlatIndex = flatIndex;

                      return (
                        <button
                          key={result.id}
                          data-selected={isSelected}
                          className={`
                            w-full flex items-center justify-between px-4 py-3 text-left
                            transition-colors
                            ${isSelected ? 'bg-white/10' : 'hover:bg-white/5'}
                          `}
                          onClick={() => {
                            result.action();
                            close();
                          }}
                          onMouseEnter={() => setSelectedIndex(currentFlatIndex)}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="min-w-0">
                              <div className="text-sm text-white truncate">{result.label}</div>
                              {result.description && (
                                <div className="text-xs text-white/40 truncate">
                                  {result.description}
                                </div>
                              )}
                            </div>
                          </div>
                          {'shortcut' in result && result.shortcut && (
                            <kbd className="ml-2 shrink-0 px-2 py-1 text-xs text-white/40 bg-white/5 rounded">
                              {result.shortcut}
                            </kbd>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Footer hint */}
            <div className="px-4 py-2 border-t border-white/10 text-xs text-white/30">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↑↓</kbd> navigieren
              </span>
              <span className="inline-flex items-center gap-1 ml-4">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↵</kbd> auswählen
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default CommandPalette;
